import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '../../../stores/tenant.store';
import { useEnrollment } from '../../../hooks/use-enrollment';
import { PersonAutocomplete } from '../../../components/PersonAutocomplete';
import { DocumentData } from 'firebase/firestore';

interface EnrollStudentDialogProps {
  open: boolean;
  onClose: () => void;
  section: DocumentData | null;
  students: DocumentData[];
}

// Machine-readable block reasons emitted by the callable (HttpsError details).
const REASON_OUTSIDE_WINDOW = 'enrollment.outside_window';
const REASON_PREREQ_UNMET = 'enrollment.prerequisites_unmet';

type Gate = 'none' | 'window' | 'prereq';

export function EnrollStudentDialog({
  open,
  onClose,
  section,
  students,
}: EnrollStudentDialogProps) {
  const { t } = useTranslation();
  const { currentTenantId } = useTenantStore();
  const { enrollStudent, isEnrollingStudent } = useEnrollment();

  const [studentId, setStudentId] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Override gating. A block comes back with a machine reason; reveal a reason
  // field for that gate. The window and prereq gates are checked in sequence by
  // the backend, so clearing the window can surface a NEW prereq gate — shown as
  // a distinct step, not a failure of the first override (`escalated`).
  const [gate, setGate] = useState<Gate>('none');
  const [reason, setReason] = useState('');
  const [windowReason, setWindowReason] = useState(''); // persisted once the window gate is cleared
  const [escalated, setEscalated] = useState(false); // window → prereq second gate
  // Inscrito, pero sin cobro porque la persona no tiene programa (H4): se avisa
  // en vez de cerrar en silencio.
  const [noChargeNotice, setNoChargeNotice] = useState(false);

  const close = () => {
    setStudentId('');
    setError(null);
    setGate('none');
    setReason('');
    setWindowReason('');
    setEscalated(false);
    setNoChargeNotice(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTenantId || !section || !studentId) return;
    if (gate !== 'none' && !reason.trim()) return;

    setError(null);
    try {
      const payload: any = { tenantId: currentTenantId, sectionId: section.id, studentId };
      // Carry the window override forward (the gate is re-checked on every call),
      // plus the prereq override when that gate is the one being authorized.
      const wr = gate === 'window' ? reason.trim() : windowReason;
      if (wr) payload.windowOverride = { reason: wr };
      if (gate === 'prereq' && reason.trim())
        payload.prerequisiteOverride = { reason: reason.trim() };

      const res = await enrollStudent(payload);
      if (res?.noCharge === 'no_program') {
        setNoChargeNotice(true);
        return;
      }
      close();
    } catch (err: any) {
      const code = err?.details?.reason as string | undefined;
      const msg = String(err?.message ?? t('sis:enrollStudent.errors.generic'));

      if (code === REASON_OUTSIDE_WINDOW) {
        setGate('window');
      } else if (code === REASON_PREREQ_UNMET) {
        if (gate === 'window') {
          // Window cleared; the prerequisite gate is new. Persist the window
          // reason, reset the field, and present it as a distinct second step.
          setWindowReason(reason.trim());
          setReason('');
          setEscalated(true);
        }
        setGate('prereq');
      }
      setError(msg);
    }
  };

  const overriding = gate !== 'none';
  const reasonLabel =
    gate === 'window'
      ? t('sis:enrollStudent.window.reasonLabel')
      : t('sis:enrollStudent.prereq.reasonLabel');
  const reasonHelp =
    gate === 'window'
      ? t('sis:enrollStudent.window.reasonHelp')
      : t('sis:enrollStudent.prereq.reasonHelp');

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('sis:enrollStudent.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* When the window override cleared and a prereq gate appeared, lead
                with a distinct notice so the admin sees a new step, not a failure. */}
            {escalated && gate === 'prereq' && (
              <Alert severity="info">{t('sis:enrollStudent.escalation.windowToPrereq')}</Alert>
            )}
            {noChargeNotice && (
              <Alert severity="warning">{t('sis:enrollStudent.noChargeNoProgram')}</Alert>
            )}
            {error && <Alert severity={overriding ? 'warning' : 'error'}>{error}</Alert>}

            <PersonAutocomplete
              people={
                students as unknown as {
                  id: string;
                  firstName?: string;
                  lastName?: string;
                  email?: string;
                }[]
              }
              value={studentId}
              onChange={setStudentId}
              label={t('sis:enrollStudent.student')}
              placeholder={t('sis:enrollStudent.studentPlaceholder')}
              noOptionsText={t('sis:enrollStudent.noStudents')}
              required
            />

            {overriding && (
              <TextField
                label={reasonLabel}
                required
                fullWidth
                multiline
                minRows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                helperText={reasonHelp}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={close} disabled={isEnrollingStudent}>
            {noChargeNotice ? t('sis:enrollStudent.close') : t('sis:enrollStudent.cancel')}
          </Button>
          {!noChargeNotice && (
            <Button
              type="submit"
              variant="contained"
              color={overriding ? 'warning' : 'primary'}
              disabled={isEnrollingStudent || !studentId || (overriding && !reason.trim())}
              sx={{ minWidth: 120 }}
            >
              {isEnrollingStudent
                ? t('sis:enrollStudent.submitting')
                : overriding
                  ? t('sis:enrollStudent.submitOverride')
                  : t('sis:enrollStudent.submit')}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
